import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { WorkOrder } from "@shared/models/work-order";
import { WorkOrderStatus } from "@shared/models/status";
import { FormBuilder, FormGroup } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MultiSelectSearchModes, MultiSelectSearchTypes } from "@shared/models/multi-select-search";

@Component({
    selector: 'app-multi-select-search',
    templateUrl: './multi-select-search.component.html',
    styleUrls: ['./multi-select-search.component.scss'],
    standalone: false
})
export class MultiSelectSearchComponent implements OnInit {

  @Input() workOrders: WorkOrder[] = [];
  @Input() inverters: WorkOrder[] = [];
  @Input() type3WorkOrders: WorkOrder[] = [];
  @Input() powerLineWorkOrders: WorkOrder[] = [];
  @Input() reports: Report[] = [];
  @Input() states: WorkOrderStatus[] = [];
  @Input() showModes = true;
  @Input() selectedMode: MultiSelectSearchModes | null = null;
  @Input() showTypes = false;
  @Input() selectedTypeId: MultiSelectSearchTypes | null = null;
  @Input() allowedModes: MultiSelectSearchModes[] = [
    MultiSelectSearchModes.REPORTED_ANY,
    MultiSelectSearchModes.REPORTED_ANY_NOT
  ];

  @Output() onChange = new EventEmitter<{ type: number, workOrders: WorkOrder[]}>();

  private takeUntilDestroyed$ = takeUntilDestroyed();
  private combinedWorkOrders: WorkOrder[] = [];

  public form!: FormGroup;
  public formFilters: { type: number, status: string, title?: string }[] = [];
  public formFiltersMap: { [key: number]: { [key: string]: WorkOrderStatus } } = {};
  public selectedFilters: WorkOrderStatus[] = [];
  public workOrdersFiltered: WorkOrder[] = [];
  public workOrderCountsMap: { [key: number]: { [key: number | string]: number } } = {};
  public searchTypes = MultiSelectSearchTypes;
  public searchModes = MultiSelectSearchModes;
  public expanded = false;
  public showModesToggle = false;
  public showTypesToggle = false;

  constructor(
    private fb: FormBuilder,
  ) { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.combineData();

    if (this.states?.length) {
      this.states = this.states.filter(s =>
        !['aloittamatta', 'not started', 'käyttöönotettu', 'done'].includes(s.definition.toLowerCase())
      );

      for (const s of this.states) {
        if (!s.workorders) continue;
        s.workorders.doneidsmap = new Map((s.workorders.doneids || []).map((id: number) => [id, true]));
        s.workorders.interruptedidsmap = new Map((s.workorders.interruptedids || []).map((id: number) => [id, true]));
      }
    }

    if (!this.selectedTypeId || !this.hasWorkOrdersFor(this.selectedTypeId)) {
      if (this.workOrders?.length) this.selectedTypeId = MultiSelectSearchTypes.WORK_ORDER;
      else if (this.inverters?.length) this.selectedTypeId = MultiSelectSearchTypes.INVERTER;
      else if (this.type3WorkOrders?.length) this.selectedTypeId = MultiSelectSearchTypes.TYPE3;
      else if (this.powerLineWorkOrders?.length) this.selectedTypeId = MultiSelectSearchTypes.POWERLINE;
    }

    if (changes['states']?.currentValue) {
      this.initFormGroup();
    }
  }


  private hasWorkOrdersFor(type: MultiSelectSearchTypes): boolean {
    switch (type) {
      case MultiSelectSearchTypes.WORK_ORDER: return this.workOrders?.length > 0;
      case MultiSelectSearchTypes.INVERTER: return this.inverters?.length > 0;
      case MultiSelectSearchTypes.TYPE3: return this.type3WorkOrders?.length > 0;
      case MultiSelectSearchTypes.POWERLINE: return this.powerLineWorkOrders?.length > 0;
      default: return false;
    }
  }

  private combineData(): void {
    this.combinedWorkOrders = [
      ...this.workOrders.map(order => ({ ...order })),
      ...this.inverters.map(order => ({ ...order })),
      ...this.type3WorkOrders.map(order => ({ ...order })),
      ...this.powerLineWorkOrders.map(order => ({ ...order })),
    ];

    //this.setWorkOrderCounts();
  }

  private setWorkOrderCounts(): void {
    this.workOrderCountsMap = {};
    if (!this.states?.length || !this.combinedWorkOrders?.length) return;

    // group states by type, and pre-init per-state buckets
    const statesByType = new Map<number, WorkOrderStatus[]>();
    for (const st of this.states) {
      if (!statesByType.has(st.type)) statesByType.set(st.type, []);
      statesByType.get(st.type)!.push(st);
    }

    // ensure maps (in case they weren't built earlier)
    for (const st of this.states) {
      const wo = st.workorders || (st.workorders = {} as any);
      if (!wo.doneidsmap) wo.doneidsmap = new Map((wo.doneids || []).map((id: number) => [id, true]));
      if (!wo.interruptedidsmap) wo.interruptedidsmap = new Map((wo.interruptedids || []).map((id: number) => [id, true]));
    }

    // track unique "reported" per type to compute type-level reported_any safely
    const reportedSetByType = new Map<number, Set<number>>();

    // pre-init structure
    for (const [type, sts] of statesByType.entries()) {
      if (!this.workOrderCountsMap[type]) {
        this.workOrderCountsMap[type] = { all: 0, reported_any: 0, reported_any_not: 0 };
      }
      // make sure every state's numeric code has a counter (starts at 0)
      for (const st of sts) {
        this.workOrderCountsMap[type][st.state] = this.workOrderCountsMap[type][st.state] || 0;
      }
      reportedSetByType.set(type, new Set<number>());
    }

    // walk all work orders
    for (const wo of this.combinedWorkOrders) {
      const type = Number(wo.type);
      const typeBucket = this.workOrderCountsMap[type];
      const sts = statesByType.get(type);
      if (!typeBucket || !sts) continue;

      typeBucket.all++;

      let matchedAnyState = false;

      // count per-state matches (done OR interrupted)
      for (const st of sts) {
        const isDone   = st.workorders?.doneidsmap?.has(wo.id);
        const isInterr = st.workorders?.interruptedidsmap?.has(wo.id);
        if (isDone || isInterr) {
          this.workOrderCountsMap[type][st.state] = (this.workOrderCountsMap[type][st.state] || 0) + 1;
          matchedAnyState = true;
        }
      }

      if (matchedAnyState) reportedSetByType.get(type)!.add(wo.id);
    }

    // finalize type-level totals
    for (const [type, set] of reportedSetByType.entries()) {
      const bucket = this.workOrderCountsMap[type];
      bucket.reported_any = set.size;
      bucket.reported_any_not = bucket.all - bucket.reported_any;
    }
  }

  public getWorkOrderCount(type: number, state?: number): number {
    const bucket = this.workOrderCountsMap[type];
    if (!bucket) return 0;

    const mode = this.getSelectedMode();

    if (state == null) return bucket.all || 0;

    if (mode === this.searchModes.REPORTED_ANY) {
      return bucket[state] || 0;
    }

    if (mode === this.searchModes.REPORTED_ANY_NOT) {
      const doneInThisState = bucket[state] || 0;
      return (bucket.all || 0) - doneInThisState;
    }

    // No mode selected → default to per-state done (or choose 0)
    return bucket[state] || 0;
  }

  /**
   * Build form controllers -> { filters: { [type]: { [definition]: boolean } } }
   * @private
   */
  private initFormGroup(): void {
    let formFields = {};

    this.states = this.states
      .filter(s => s.state !== 1 && s.definition.toLowerCase() !== 'not started' && s.definition.toLowerCase() !== 'aloittamatta')
      .filter(s => s.type === 2 || s.type === 4);

    this.states.sort((a, b) => a.id - b.id);

    this.states.forEach(state => {
      const key = this.toUnderscore(state.definition);

      if (!formFields[state.type]) formFields[state.type] = {};
      formFields[state.type][key] = [false];

      if (!this.formFiltersMap[state.type]) this.formFiltersMap[state.type] = {};

      if (state.workorders) {
        state.workorders.doneidsmap = new Map(state.workorders.doneids.map(id => [id, true]));
        state.workorders.interruptedidsmap = state.workorders.interruptedids
          ? new Map(state.workorders.interruptedids.map(id => [id, true]))
          : new Map();
      }

      this.formFiltersMap[state.type][key] = state;
    });

    Object.keys(formFields).forEach(type => {
      formFields[type] = this.fb.group(formFields[type]);
    });

    const form = {
      selected_type: [0],
      select_all: true,
      reported_any: false,
      reported_any_not: false,
      filters: this.fb.group(formFields),
    };

    this.form = this.fb.group(form);

    this.onFormValueChange();
      if (this.selectedTypeId) this.form.get('selected_type')?.setValue(this.selectedTypeId);
      //if (this.selectedMode) this.form.get(this.selectedMode)?.setValue(true);

      setTimeout(() => {
        this.workOrdersFiltered = this.combinedWorkOrders.filter(
          wo => wo.type === this.form.get('selected_type')?.value
        );
        this.onChange.emit({
          type: this.form.get('selected_type')?.value,
          workOrders: this.workOrdersFiltered,
        });
        this.setWorkOrderCounts();
      })
    }

    private onFormValueChange(): void {
    this.form.valueChanges
      .pipe(this.takeUntilDestroyed$)
      .subscribe((formValues: any) => {

        const selectedType = this.form.get('selected_type')?.value;

        // any state checkbox selected for the selected type?
        const typeGroup = formValues?.filters?.[selectedType] || {};
        const hasStateFilterSelected = Object.values(typeGroup).some((v: any) => v === true);

        const noModeSelected =
          !this.form.get(this.searchModes.REPORTED_ANY)?.value &&
          !this.form.get(this.searchModes.REPORTED_ANY_NOT)?.value &&
          !this.form.get(this.searchModes.REPORTED_CURRENT)?.value &&
          !this.form.get(this.searchModes.REPORTED_CURRENT_NOT)?.value;

        // default to "Vaihe valmis" when a state is selected but no mode chosen
        if (hasStateFilterSelected && noModeSelected) {
          this.form.get(this.searchModes.REPORTED_ANY)?.setValue(true, { emitEvent: false });
        }

        // deselect "Show all" when any state is selected
        if (hasStateFilterSelected) {
          this.form.get('select_all')?.setValue(false, { emitEvent: false });
        }

        // no states selected → show all, clear modes, select "Show all"
        if (!hasStateFilterSelected) {
          this.form.patchValue({
            reported_any: false,
            reported_any_not: false,
            reported_current: false,
            reported_current_not: false,
            select_all: true,
          }, { emitEvent: false });
        }

        this.setFormFilters(selectedType);

        // Build selectedStates uniformly for all types
        const map = this.getSelectedFiltersData(formValues['filters'][selectedType]) || new Map<string, WorkOrderStatus>();
        const selectedStates: WorkOrderStatus[] = [...map.values()];
        this.selectedFilters = selectedStates;

        // Compute results
        if (!hasStateFilterSelected) {
          this.workOrdersFiltered = this.combinedWorkOrders.filter(wo => Number(wo.type) === Number(selectedType));
        } else {
          this.workOrdersFiltered = this.filter(selectedStates);
        }

        this.onChange.emit({
          type: selectedType,
          workOrders: this.workOrdersFiltered,
        });
      });
  }



  /**
   * Set available statuses for the selected work order type & show them as filters
   * @param type
   * @private
   */
  private setFormFilters(type: number): void {
    this.formFilters = [];
    const group = this.getFiltersByType(type);
    if (!group) return;

    this.formFilters = Object.keys(group.controls).map((key, index) => ({
      type,
      status: key,
      // If you still want a header row for powerlines in the UI, you can conditionally add:
      title: (type === MultiSelectSearchTypes.POWERLINE && index === 0) ? 'powerline_title' : undefined
    }));

    this.formFilters.sort((a, b) => {
      const stateA = this.formFiltersMap[a.type]?.[a.status]?.state ?? 0;
      const stateB = this.formFiltersMap[b.type]?.[b.status]?.state ?? 0;
      return stateA - stateB;
    });
  }

  private getFiltersByType(type: number): FormGroup {
    return this.form.get('filters')?.get(String(type)) as FormGroup;
  }

  public getFilter(type: number, filterKey: string): any {
    return this.getFiltersByType(type).get(filterKey);
  }

  public filtersExist(type: number): boolean {
    return !!this.getFiltersByType(type);
  }

  public getSelectedType(): boolean {
    return this.form.get('selected_type')?.value;
  }

  private toUnderscore(str: string): string {
    return str
      .normalize('NFD')                     // Normalize to separate base letter and diacritic
      .replace(/[\u0300-\u036f]/g, '')      // Remove diacritics
      .replace(/\s+/g, '_')                 // Replace whitespace with underscore
      .toLowerCase();
  }

  private filter(selectedStatesParam?: WorkOrderStatus[]): WorkOrder[] {
    const results: WorkOrder[] = [];
    const selectedType = this.form.get('selected_type')?.value;

    const selectedStates = (selectedStatesParam && selectedStatesParam.length)
      ? selectedStatesParam
      : this.states.filter(s => s.type === selectedType); // fallback: all states of this type

    for (const workOrder of this.combinedWorkOrders) {
      const typeMatch =
        (selectedType === this.searchTypes.WORK_ORDER && Number(workOrder.type) === 1) ||
        (selectedType === this.searchTypes.INVERTER && Number(workOrder.type) === 2) ||
        (selectedType === this.searchTypes.TYPE3 && Number(workOrder.type) === 3) ||
        (selectedType === this.searchTypes.POWERLINE && Number(workOrder.type) === 4);
      if (!typeMatch) continue;

      const filters = this.getFiltersByType(workOrder.type);
      if (!filters) continue;

      // no checkboxes → show all of this type
      if (!this.isFilterSelected(filters) &&
          !this.getSelectedMode()) {
        results.push(workOrder);
        continue;
      }

      // REPORTED_ANY
      if (this.form.get(this.searchModes.REPORTED_ANY)?.value) {
        const reportedInAny = selectedStates.some(st =>
          st.workorders?.doneidsmap?.has(workOrder.id) ||
          st.workorders?.interruptedidsmap?.has(workOrder.id)
        );
        if (reportedInAny) results.push(workOrder);
        continue;
      }

      // REPORTED_ANY_NOT — ONLY against the selected states
      if (this.form.get(this.searchModes.REPORTED_ANY_NOT)?.value) {
        const reportedInAny = selectedStates.some(st =>
          st.workorders?.doneidsmap?.has(workOrder.id) ||
          st.workorders?.interruptedidsmap?.has(workOrder.id)
        );
        if (!reportedInAny) results.push(workOrder);
        continue;
      }

      // plain status filter (no mode)
      for (const key of Object.keys(filters.controls)) {
        if (!filters.get(key)?.value) continue;
        const status = this.formFiltersMap[workOrder.type]?.[key];
        if (!status) continue;
        const workOrderStatusId = workOrder?.priority_status?.id || workOrder.status_id;
        if (workOrderStatusId === status.id) {
          results.push(workOrder);
          break;
        }
      }
    }

    return this.removeDuplicates(results);
  }

  private isFilterSelected(_: FormGroup): boolean {
    const selectedType = this.form.get('selected_type')?.value;
    const group = this.getFiltersByType(selectedType);
    if (!group) return false;
    return Object.values(group.controls).some((c: any) => c.value === true);
  }

  private removeDuplicates(workOrders: WorkOrder[]): WorkOrder[] {
    const results = new Map<number, WorkOrder>();

    for (const workOrder of workOrders) {
      results.set(workOrder.id, workOrder);
    }

    return Array.from(results.values());
  }

  setSearchType(value: number) {
    const currentValue = this.form.value.selected_type;
    this.form.get('selected_type')?.setValue(value);
  }

  toggleExpand(): void {
    this.expanded = !this.expanded;
  }

  selectAll(): void {
    const control = this.form.get('select_all');
    if (!control) return;

    control.setValue(true, { emitEvent: false });
    this.resetModes();

    const filters = this.form.get('filters') as FormGroup;
    for (const type of Object.keys(filters.controls)) {
      const group = filters.get(type) as FormGroup;
      for (const key of Object.keys(group.controls)) {
        group.get(key)?.setValue(false, { emitEvent: false });
      }
    }

    this.selectedFilters = [];

    const selectedType = this.form.get('selected_type')?.value;
    this.workOrdersFiltered = this.combinedWorkOrders.filter(
      wo => wo.type === selectedType
    );

    this.onChange.emit({
      type: selectedType,
      workOrders: this.workOrdersFiltered,
    });

    this.form.updateValueAndValidity({ emitEvent: true });
  }

  onSelectAllRowClick(evt: MouseEvent): void {
    const clickedInput = (evt.target as HTMLElement)?.closest('input.filter-checkbox');
    if (clickedInput) return;

    const control = this.form.get('select_all');
    control?.setValue(true, { emitEvent: false });
    this.selectAll();
  }


  private resetModes(): void {
    this.form.get('select_all')?.setValue(false, {emitEvent: false});
    this.form.get(MultiSelectSearchModes.REPORTED_ANY)?.setValue(false, {emitEvent: false});
    this.form.get(MultiSelectSearchModes.REPORTED_CURRENT)?.setValue(false, {emitEvent: false});
    this.form.get(MultiSelectSearchModes.REPORTED_ANY_NOT)?.setValue(false, {emitEvent: false});
    this.form.get(MultiSelectSearchModes.REPORTED_CURRENT_NOT)?.setValue(false, {emitEvent: false});
  }

  setFilterMode(mode: string, value: boolean): void {
    this.resetModes();
    this.form.get(mode)?.setValue(value, { emitEvent: false });

    const selectedType = this.form.get('selected_type')?.value;
    const filters = this.getFiltersByType(selectedType);

    if (filters) {
      for (const key of Object.keys(filters.controls)) {
        filters.get(key)?.setValue(false, { emitEvent: false });
      }

      const firstKey = Object.keys(filters.controls)[0];
      if (firstKey) {
        filters.get(firstKey)?.setValue(true, { emitEvent: false });
      }
    }

    this.form.get('select_all')?.setValue(false, { emitEvent: false });

    this.form.updateValueAndValidity({ emitEvent: true });
    this.toggleMode();
  }

  filterChanged(): void {
    this.form.get('select_all')?.setValue(false, { emitEvent: false });
  }


  selectAllRowClick(evt: MouseEvent): void {
    const inputClicked = (evt.target as HTMLElement).closest('input.filter-checkbox');
    if (inputClicked) return; // checkbox click handled separately

    const control = this.form.get('select_all');
    const next = !control?.value;
    control?.setValue(next, { emitEvent: false });
    if (next) this.selectAll();
  }

  selectMode(mode: string): void {
    // Reset all modes
    this.form.get(this.searchModes.REPORTED_ANY)?.setValue(false, { emitEvent: false });
    this.form.get(this.searchModes.REPORTED_ANY_NOT)?.setValue(false, { emitEvent: false });
    this.form.get(this.searchModes.REPORTED_CURRENT)?.setValue(false, { emitEvent: false });
    this.form.get(this.searchModes.REPORTED_CURRENT_NOT)?.setValue(false, { emitEvent: false });

    // Enable clicked mode
    this.form.get(mode)?.setValue(true, { emitEvent: false });

    // Deselect "Show all"
    this.form.get('select_all')?.setValue(false, { emitEvent: false });

    // Get current filters for the selected type
    const selectedType = this.form.get('selected_type')?.value;
    const filters = this.getFiltersByType(selectedType);

    if (filters) {
      // Clear all filters
      for (const key of Object.keys(filters.controls)) {
        filters.get(key)?.setValue(false, { emitEvent: false });
      }

      // ✅ Select first state by default
      const firstKey = Object.keys(filters.controls)[0];
      if (firstKey) {
        filters.get(firstKey)?.setValue(true, { emitEvent: false });
      }
    }

    this.form.updateValueAndValidity({ emitEvent: true });
  }

  toggleFilter(filterKey: string, type?: MultiSelectSearchTypes): void {
    const filter = type
      ? this.getFilter(
          type,
          filterKey
        )
      : this.getFilter(
          this.form.get('selected_type')?.value,
          filterKey
        );

    if (!filter) return;
    filter.setValue(!filter.value);
  }

  /**
   * Return status & work order data for currently selected filters
   */
  private getSelectedFiltersData(filters: { [key: string]: boolean }): Map<string, WorkOrderStatus> | null {
    if (!filters) return null;

    const selectedType = this.form.get('selected_type')?.value;
    const result = new Map<string, WorkOrderStatus>();

    for (const key of Object.keys(filters)) {
      if (filters[key]) {
        const status = this.formFiltersMap[selectedType]?.[key];
        if (status) result.set(key, status);
      }
    }
    return result;
  }

  public getFilterStatus(filterKey: string): WorkOrderStatus | null {
    const selectedType = this.form.get('selected_type')?.value;
    return this.formFiltersMap[selectedType]?.[filterKey] ?? null;
  }

  public toggleMode(): void {
    this.showModesToggle = !this.showModesToggle;
  }

  public toggleType(): void {
    this.showTypesToggle = !this.showTypesToggle;
  }

  public getSelectedMode(): string | null {
    if (this.form.get(MultiSelectSearchModes.REPORTED_ANY)?.value) {
      return MultiSelectSearchModes.REPORTED_ANY;
    }

    if (this.form.get(MultiSelectSearchModes.REPORTED_CURRENT)?.value) {
      return MultiSelectSearchModes.REPORTED_CURRENT;
    }

    if (this.form.get(MultiSelectSearchModes.REPORTED_ANY_NOT)?.value) {
      return MultiSelectSearchModes.REPORTED_ANY_NOT;
    }

    if (this.form.get(MultiSelectSearchModes.REPORTED_CURRENT_NOT)?.value) {
      return MultiSelectSearchModes.REPORTED_CURRENT_NOT;
    }

    return null;
  }

  public toggleSelectedMode(mode: string): void {
    if (this.form.get(mode)?.value) {
      this.resetModes();
      this.form.updateValueAndValidity({ emitEvent: true });
      this.toggleMode();
      return;
    }

    this.resetModes();
    this.form.get(mode)?.setValue(!this.form.get(mode)?.value);
    this.toggleMode();
  }

  public isPowerlines(): boolean {
    return this.form.get('selected_type')?.value === MultiSelectSearchTypes.POWERLINE;
  }

  ngOnDestroy(): void {
  }

}
